import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { ProductStatus } from './ProductStatus';
import { BrandDiscounts } from './BrandDiscounts';
import { useAdmin } from '../AdminContext';
import { skinTypes } from '../../data/concerns';
import { asSkinType, errorCount, productChecks } from './catalogRules';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { useCan } from '../useCan';
import { useCosts } from '../margin/useCosts';
import { MarginBadge } from '../margin/MarginBadge';
import { productMargin } from '../../core/rules/margin';
import { vatRateOf } from '../../core/rules/revenue';
import type { Product } from '../../core/types/catalog';

/** De parameters in het webadres die deze lijst filteren. */
const FILTER_KEYS = ['merk', 'thema', 'filter'] as const;

/** Alle producten, te filteren op merk, huidthema en problemen. De filters staan in het webadres. */
export function Products() {
  const {
    catalog
  } = useAdmin();
  // Marge alleen voor wie hem mag zien (Sarah, Boekhouder); één gedeelde aanvraag voor de hele lijst.
  const {
    canAll
  } = useCan();
  const showMargin = canAll('finance.margin');
  const {
    data: costs
  } = useCosts(showMargin);
  const marginOf = (p: Product) => costs ? productMargin(Math.round(p.price * 100), vatRateOf(p), costs.itemCosts[p.id]).percent : null;
  const marginKnown = (p: Product) => costs?.itemCosts[p.id] !== undefined;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // De zoekterm staat ook in het adres: na een product openen en Terug staat hij er nog.
  const query = params.get('zoek') ?? '';
  const brand = params.get('merk') ?? '';
  const tag = asSkinType(params.get('thema') ?? '');
  const onlyIncomplete = params.get('filter') === 'onvolledig';
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);else next.delete(key);
    setParams(next, {
      replace: true
    });
  };
  const setQuery = (value: string) => setParam('zoek', value);

  /** De filters die nu iets wegfilteren, in gewone woorden. */
  const activeFilters = [brand, tag ?? '', onlyIncomplete ? 'alleen met problemen' : ''].filter(Boolean);

  /** Haalt merk, thema en "met problemen" uit het adres; andere parameters blijven staan. */
  const clearFilters = () => {
    const next = new URLSearchParams(params);
    for (const key of FILTER_KEYS) next.delete(key);
    setParams(next, {
      replace: true
    });
  };

  // De controles hangen alleen van de producten af, niet van de zoekterm: één keer per wijziging.
  const checked = useMemo(() => catalog.products.map(p => ({
    p,
    checks: productChecks(p),
    text: `${p.name} ${p.brand} ${p.category} ${p.keyIngredients.join(' ')}`.toLowerCase()
  })), [catalog.products]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return checked.filter(({
      p
    }) => !brand || p.brand === brand).filter(({
      p
    }) => !tag || p.skinTypes.includes(tag)).filter(r => !q || r.text.includes(q)).filter(r => !onlyIncomplete || errorCount(r.checks) > 0);
  }, [checked, brand, tag, query, onlyIncomplete]);
  return <div data-id="element-776" data-mp="1ec029zefime">
      <PageHeader title="Producten" description={`${plural(catalog.products.length, 'product', 'producten')} in de webshop. Klik op een product om het aan te passen.`} actions={<Link to="/be/producten/nieuw" className={btnPrimary} data-id="element-778" data-mp="3ojjtu80ffvo4">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-779" data-mp="1qt0cgb7zp0kf" />
            Nieuw product
          </Link>} data-id="element-777" data-mp="1dj0bsyuxtdr" />

      <div className="mb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center" data-id="element-780" data-mp="1msgghaz5k2zv">
        <label className="relative col-span-2 min-w-[14rem] flex-1" data-id="element-781" data-mp="1tubxu7o93pkt">
          <span className="sr-only" data-id="element-782" data-mp="1ytog0f46g8h2">Zoeken</span>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-783" data-mp="1ysdgirhrlmvr" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Zoek op naam, merk of ingrediënt…" className={`${inputCls} pl-9`} data-id="element-784" data-mp="1ov8poecz538m" />
        </label>
        <select value={brand} onChange={e => setParam('merk', e.target.value)} className={`${inputCls} sm:w-auto`} aria-label="Merk" data-id="element-785" data-mp="1b20y2mow87qx">
          <option value="" data-id="element-786" data-mp="1167l5279ic8m">Alle merken</option>
          {catalog.brands.map(b => <option key={b.name} data-id="element-787" data-mp="1650ucifinh2z">{b.name}</option>)}
        </select>
        <select value={tag ?? ''} onChange={e => setParam('thema', e.target.value)} className={`${inputCls} sm:w-auto`} aria-label="Huidthema" data-id="element-788" data-mp="162y5i1q3ntzu">
          <option value="" data-id="element-789" data-mp="x4m5xa799lv9">Alle huidthema’s</option>
          {skinTypes.map(s => <option key={s} data-id="element-790" data-mp="di137bc4rlad">{s}</option>)}
        </select>
        <button type="button" aria-pressed={onlyIncomplete} onClick={() => setParam('filter', onlyIncomplete ? '' : 'onvolledig')} className={`col-span-2 rounded-full border px-4 py-2.5 text-[14px] transition-colors duration-150 ease-soft sm:py-2 sm:text-[13px] ${onlyIncomplete ? 'border-ink bg-ink text-shell' : 'border-line bg-white text-ink hover:border-ink/40'}`} data-id="element-791" data-mp="dkdsyao4qzxz">
          Alleen met problemen
        </button>
      </div>

      <p className="mb-2 text-[13px] text-muted md:hidden" role="status" data-id="element-792" data-mp="rghgxm8euc20">
        {plural(rows.length, 'resultaat', 'resultaten')}
      </p>
      <div className="overflow-hidden rounded-lg border border-line bg-white" data-id="element-793" data-mp="117vg1swg99um">
        {/* Mobiel: één product per rij, groot genoeg voor je duim */}
        <ul className="divide-y divide-line md:hidden" data-id="element-794" data-mp="1f5jyyv6yi6x1">
          {rows.map(({
          p,
          checks
        }) => <li key={p.id} data-id="element-795" data-mp="1a6ywep48fb5f">
              <Link to={`/be/producten/${p.id}`} className="flex items-center gap-3 px-4 py-3 active:bg-shell" data-id="element-796" data-mp="7vxbh6ructj8">
                {p.image ? <ProductImage src={p.image} width={56} loading="lazy" className="h-14 w-14 shrink-0 rounded p-1" data-id="element-797" data-mp="cv03nqmq359s" /> : <span className="h-14 w-14 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-798" data-mp="lmscxy00g8cs" />}
                <span className="min-w-0 flex-1" data-id="element-799" data-mp="qm57z611clqg">
                  <span className="block truncate text-[15px] text-ink" data-id="element-800" data-mp="qldhg9mape71">{p.name || 'Naamloos product'}</span>
                  <span className="block truncate text-[13px] text-muted" data-id="element-801" data-mp="vj5870b7eu9n">
                    {p.brand} · {p.step ?? p.category}
                    {showMargin && costs && marginKnown(p) && <>
                        {' · '}
                        <MarginBadge percent={marginOf(p)} data-id="element-802" data-mp="32c7nr3whfdt9" /> marge
                      </>}
                  </span>
                  <span className="mt-0.5 block" data-id="element-803" data-mp="36db21lyvt1sd">
                    <ProductStatus product={p} checks={checks} data-id="element-804" data-mp="2yu1s9rmgr12s" />
                  </span>
                </span>
                <span className="shrink-0 text-[15px] tabular-nums text-ink" data-id="element-805" data-mp="2u2jn0rdeqec0">{euro(p.price, {
                cents: true
              })}</span>
              </Link>
            </li>)}
        </ul>
        <div className="hidden overflow-x-auto md:block" data-id="element-806" data-mp="2l2dhpsrv4knx">
          <table className="w-full min-w-[760px] text-left text-[14px]" data-id="element-807" data-mp="2l3y9jamslpi3">
            <thead className="border-b border-line bg-shell text-[12px] text-muted" data-id="element-808" data-mp="3hrb86yskxwfa">
              <tr data-id="element-809" data-mp="3ct1giy62ddyf">
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-810" data-mp="3cukvmlfzhiym">Product</th>
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-811" data-mp="38qwno1nfwlcu">Stap</th>
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-812" data-mp="33sv5dixu8q2f">Huidthema’s</th>
                <th scope="col" className="px-4 py-3 text-right font-normal" data-id="element-813" data-mp="33uxrvszs812e">Prijs</th>
                {showMargin && <th scope="col" className="px-4 py-3 text-right font-normal" data-id="element-814" data-mp="1sdpsjm3ztb53">
                    <abbr title="Brutomarge: verkoop zonder btw min de gemiddelde inkoop" className="no-underline" data-id="element-815" data-mp="3sfp8nb2rimd2">
                      Marge
                    </abbr>
                  </th>}
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-816" data-mp="3ihrl6psq8tli">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-817" data-mp="3ng2w73urda5b">
              {rows.map(({
              p,
              checks
            }) =>
            // De hele rij is klikbaar voor de muis; de link in de eerste cel is er voor toetsenbord en schermlezer.
            <tr key={p.id} onClick={() => navigate(`/be/producten/${p.id}`)} className="cursor-pointer transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-818" data-mp="jyuimw72cwge">
                  <td className="px-4 py-3" data-id="element-819" data-mp="a9t294wlwd1w">
                    <Link to={`/be/producten/${p.id}`} onClick={e => e.stopPropagation()} className="flex items-center gap-3" data-id="element-820" data-mp="a8hwx0niix8o">
                      {p.image ? <ProductImage src={p.image} width={44} loading="lazy" className="h-11 w-11 shrink-0 rounded p-0.5" data-id="element-821" data-mp="12qn9ol2ko7lh" /> : <span className="h-11 w-11 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-822" data-mp="2neo8hlrjnt1c" />}
                      <span className="min-w-0" data-id="element-823" data-mp="2w92x4exysfx4">
                        <span className="block truncate text-ink" data-id="element-824" data-mp="2w7t0zvao9ht4">{p.name || 'Naamloos product'}</span>
                        <span className="block text-[12px] text-muted" data-id="element-825" data-mp="2s80z21fsa9wq">
                          {p.brand} · {p.category}
                        </span>
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted" data-id="element-826" data-mp="1zl6ez5mvpcjs">{p.step ?? '—'}</td>
                  <td className="px-4 py-3" data-id="element-827" data-mp="2dk61a6mx5fcs">
                    <span className="flex flex-wrap gap-1" data-id="element-828" data-mp="28jru0qt569ft">
                      {p.skinTypes.map(s => <span key={s} className="rounded-full bg-sand px-2 py-0.5 text-[12px] text-ink" data-id="element-829" data-mp="3ox8i92sxk6xh">
                          {s}
                        </span>)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-ink" data-id="element-830" data-mp="1514k4axk94pm">{euro(p.price, {
                  cents: true
                })}</td>
                  {showMargin && <td className="whitespace-nowrap px-4 py-3 text-right" data-id="element-831" data-mp="vb1f1cd600ax">
                      {!costs ? <span className="text-muted" data-id="element-832" data-mp="rarck9okzs0t">…</span> : p.kind === 'giftcard' || p.kind === 'kuur' ? <span className="text-muted" data-id="element-833" data-mp="mav5u2oi1mhk">—</span> : <MarginBadge percent={marginOf(p)} data-id="element-834" data-mp="mbxvk7a35ivw" />}
                    </td>}
                  <td className="px-4 py-3" data-id="element-835" data-mp="hba81vl2fk6f">
                    <ProductStatus product={p} checks={checks} data-id="element-836" data-mp="ccyw9fb7mzlk" />
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <EmptyState icon={SearchIcon} className="m-4 border-0 bg-transparent" title="Geen producten gevonden" text={emptyText(query.trim(), activeFilters)} action={<span className="flex flex-wrap justify-center gap-2" data-id="element-838" data-mp="3knszh9c0orqk">
                {/* Eerst de zoekterm: dat is meestal de typfout. Bewust gekozen filters blijven staan. */}
                {query.trim() && <button type="button" onClick={() => setQuery('')} className={activeFilters.length ? btnPrimary : btnSecondary} data-id="element-839" data-mp="3ue44z0bcn8zk">
                    Zoekterm wissen
                  </button>}
                {activeFilters.length > 0 && <button type="button" onClick={clearFilters} className={btnSecondary} data-id="element-840" data-mp="36mypim9bmips">
                    {activeFilters.length === 1 ? `Toon zonder “${activeFilters[0]}”` : 'Filters loslaten'}
                  </button>}
              </span>} data-id="element-837" data-mp="26qzxg6u8fyot" />}
      </div>

      <div className="mt-8 max-w-2xl" data-id="element-841" data-mp="pz4d5h2abgcg">
        <BrandDiscounts data-id="element-842" data-mp="py31pbhcwru7" />
      </div>
    </div>;
}

/** Zegt precies waarbinnen er niets gevonden is, zodat Sarah ziet wat ze moet loslaten. */
function emptyText(query: string, filters: string[]) {
  const within = filters.length ? ` binnen ${filters.join(' · ')}` : '';
  if (query) return `Niets gevonden voor “${query}”${within}.`;
  return `Geen producten${within}.`;
}